import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import useLabSounds, { outputHasIosError } from "../hooks/useLabSounds";

// Pagination thresholds — matches a typical Cisco IOS terminal length 24.
const PAGE_LINES = 24;

// Module-level cache of per-(session,device) terminal state. Restored when
// the user switches back to a device they were previously typing on, so
// command history + scrollback are preserved across device switches in
// multi-device labs (bug #8). Cleared when a new lab starts because the
// LabWorkspace flips session_id, naturally moving cache keys out of scope.
const TERMINAL_CACHE = new Map();
const cacheKey = (sessionId, deviceId) => `${sessionId || "_"}:${deviceId || "single"}`;

// Phase 2 — once-per-session marker for the "live IOS syslog" tutorial toast.
// We fire it the first time the user sees a %LINK-3-UPDOWN line in their
// command output — i.e. the first successful `no shutdown` of the lab.
const SYSLOG_TOAST_FIRED = new Set();

// Cisco CLI terminal UI. The input is never disabled — we block submit when busy
// and rely on a simple in-flight guard + auto-refocus after every command.
export default function Terminal({ sessionId, banner, initialPrompt, onConfigChange, deviceId, deviceLabel }) {
  const sounds = useLabSounds();
  const lineIdRef = useRef(0);
  const nextLine = (data) => ({ id: ++lineIdRef.current, ...data });
  const key = cacheKey(sessionId, deviceId);
  const cached = TERMINAL_CACHE.get(key);

  const [lines, setLines] = useState(() =>
    cached?.lines ? cached.lines : banner ? [{ id: 0, type: "banner", text: banner }] : []
  );
  const [prompt, setPrompt] = useState(cached?.prompt ?? (initialPrompt || "Router>"));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(cached?.history ?? []);
  const [historyIdx, setHistoryIdx] = useState(-1);
  // Remaining lines waiting for SPACE / ENTER. Empty array = no paging.
  const [pendingLines, setPendingLines] = useState([]);

  // Bump the line-id counter past whatever's already in the cached scrollback
  // so newly-pushed lines don't reuse old ids.
  useEffect(() => {
    if (cached?.lines?.length) {
      lineIdRef.current = Math.max(lineIdRef.current, ...cached.lines.map((l) => l.id || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist state on every change so a future remount can restore it.
  useEffect(() => {
    TERMINAL_CACHE.set(key, { lines, prompt, history });
  }, [key, lines, prompt, history]);

  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);

  const isPaging = pendingLines.length > 0;

  // Auto-scroll to bottom on new lines.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [lines]);

  // Always keep focus on the input — first mount and whenever busy flips to false.
  useEffect(() => {
    if (!busy) {
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [busy, lines]);

  // Detect a Windows-style PC ping output and stream the reply / timeout
  // lines with a small delay so it feels like a real ping. Returns true if
  // streaming kicked in so the caller skips the default instant render.
  const tryStreamPing = (text) => {
    if (!text) return false;
    const trimmed = text.replace(/\n$/, "");
    const lines = trimmed.split("\n");
    // Find a "Pinging ... with 32 bytes of data:" header. PC simulator emits
    // a blank line before it so the header may sit at idx 0 or 1.
    const headerIdx = lines.findIndex((l) => /^Pinging .+ with 32 bytes of data:$/.test(l));
    if (headerIdx === -1) return false;
    // The 4 attempt lines (Reply / timeout) follow the header.
    const attemptIdxStart = headerIdx + 1;
    const attemptLines = [];
    for (let i = attemptIdxStart; i < lines.length && attemptLines.length < 4; i++) {
      if (/^(Reply from |Request timed out\.)/.test(lines[i])) {
        attemptLines.push(i);
      } else if (attemptLines.length > 0) {
        break;
      }
    }
    if (attemptLines.length === 0) return false;
    const firstAttempt = attemptLines[0];
    const lastAttempt = attemptLines[attemptLines.length - 1];

    // 1. Print pre-attempt block (any leading blank + header) immediately.
    const preface = lines.slice(0, firstAttempt).join("\n");
    if (preface) {
      setLines((ls) => [...ls, nextLine({ type: "out", text: preface })]);
    }

    // 2. Print each attempt line with a 150ms delay between them.
    const after = lines.slice(lastAttempt + 1).join("\n");
    let i = firstAttempt;
    const interval = setInterval(() => {
      if (i > lastAttempt) {
        clearInterval(interval);
        if (after) {
          setLines((ls) => [...ls, nextLine({ type: "out", text: after })]);
        }
        setBusy(false);
        return;
      }
      const ln = lines[i];
      setLines((ls) => [...ls, nextLine({ type: "out", text: ln })]);
      i += 1;
    }, 150);
    return true;
  };

  // Push a (possibly large) output block into the terminal. If it exceeds
  // PAGE_LINES, we render the first page + a `--More--` marker and queue the rest.
  const pushOutput = (text) => {
    if (!text) return;
    // Special-case: animate Windows-style PC ping output.
    if (tryStreamPing(text)) return "stream";
    const allLines = text.replace(/\n$/, "").split("\n");
    if (allLines.length <= PAGE_LINES) {
      setLines((ls) => [...ls, nextLine({ type: "out", text })]);
      return;
    }
    const first = allLines.slice(0, PAGE_LINES).join("\n");
    const rest = allLines.slice(PAGE_LINES);
    setLines((ls) => [
      ...ls,
      nextLine({ type: "out", text: first }),
      nextLine({ type: "more" }),
    ]);
    setPendingLines(rest);
  };

  // Reveal `n` more queued lines (n=PAGE_LINES on space, n=1 on enter).
  // Removes the existing `--More--` marker; appends a new one if more remain.
  const advancePaging = (n) => {
    setPendingLines((prev) => {
      if (prev.length === 0) return prev;
      const take = prev.slice(0, n);
      const rest = prev.slice(n);
      setLines((ls) => {
        const cleaned = ls.filter((l) => l.type !== "more");
        const next = [...cleaned, nextLine({ type: "out", text: take.join("\n") })];
        if (rest.length > 0) next.push(nextLine({ type: "more" }));
        return next;
      });
      return rest;
    });
  };

  // Abort paging — drop remaining queued lines, remove --More-- marker.
  const cancelPaging = () => {
    setPendingLines([]);
    setLines((ls) => ls.filter((l) => l.type !== "more"));
  };

  const send = async (e) => {
    e.preventDefault();
    return submitCommand(input, { restoreInputAfter: false });
  };

  // Send a command. If restoreInputAfter is true, the input field is
  // restored to its prior value after the command runs (used for the
  // immediate '?' help so the user keeps editing their partial command).
  const submitCommand = async (cmd, { restoreInputAfter = false } = {}) => {
    if (!sessionId || busy) return;
    const priorInput = input;
    // Interactive IOS questions (Password:, "How many bits in the modulus",
    // multi-line banner text) don't end in > or # — Enter alone is an answer.
    const interactive = !/[>#]\s*$/.test(prompt || "");
    const secret = /Password:\s*$/.test(prompt || "");
    if (!restoreInputAfter) {
      setInput("");
      if (cmd.trim() && !secret) {
        setHistory((h) => [cmd, ...h].slice(0, 50));
      }
      setHistoryIdx(-1);
    }

    // echo (IOS never echoes passwords)
    setLines((ls) => [...ls, nextLine({ type: "cmd", prompt, text: secret ? "" : cmd })]);
    if (!cmd.trim() && !interactive) return;

    setBusy(true);
    let streaming = false;
    try {
      const { data } = await api.post(`/labs/session/${sessionId}/command`, { command: cmd, device_id: deviceId });
      if (data.output) {
        const result = pushOutput(data.output);
        streaming = result === "stream";
        // Round 39 — audio feedback: play cmd-wrong on IOS "% Invalid…"
        // errors, cmd-right on any other successful command execution.
        // Skip blank-Enter (cmd is empty after trim guard above).
        if (outputHasIosError(data.output)) {
          sounds.playCmdWrong();
        } else {
          sounds.playCmdRight();
        }
        // Phase 2: surface a one-time toast the first time syslog appears
        // in the lab so users notice the new fidelity.
        if (sessionId && !SYSLOG_TOAST_FIRED.has(sessionId)
            && /%LINK-3-UPDOWN/.test(data.output)) {
          SYSLOG_TOAST_FIRED.add(sessionId);
          toast.success("Live IOS console messages enabled", {
            description: "You're now seeing the same %LINK / %LINEPROTO syslog Cisco devices emit on real consoles.",
            duration: 6000,
          });
        }
      }
      setPrompt(data.prompt);
      onConfigChange?.();
    } catch (err) {
      setLines((ls) => [
        ...ls,
        nextLine({ type: "err", text: err?.response?.data?.detail || "command failed" }),
      ]);
    } finally {
      // If pushOutput started a streamed animation it will clear busy itself
      // when the last line is rendered. Otherwise drop busy now.
      if (!streaming) setBusy(false);
      if (restoreInputAfter) {
        // Re-display the partial command the user was typing — like real Cisco.
        setInput(priorInput);
      }
    }
  };

  const onKeyDown = (e) => {
    // Pagination takes priority — emulates real IOS '--More--' behaviour.
    if (isPaging) {
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        advancePaging(PAGE_LINES);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        advancePaging(1);
        return;
      }
      // Any other printable key (or 'q') cancels the more-prompt — like Cisco.
      if (e.key.length === 1 || e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        cancelPaging();
        return;
      }
    }

    if (e.key === "?") {
      // Real Cisco fires help the moment '?' is pressed, without Enter.
      // We submit "<currentInput>?" and restore the input afterwards so the
      // user can continue typing their command.
      e.preventDefault();
      if (busy) return;
      submitCommand(input + "?", { restoreInputAfter: true });
      return;
    }
    if (e.key === "Tab") {
      // IOS-style completion: resolve unique prefix, or show candidate list.
      e.preventDefault();
      if (busy || !sessionId) return;
      doTabCompletion();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const nextIdx = Math.min(historyIdx + 1, history.length - 1);
      setHistoryIdx(nextIdx);
      setInput(history[nextIdx] || "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIdx = Math.max(historyIdx - 1, -1);
      setHistoryIdx(nextIdx);
      setInput(nextIdx === -1 ? "" : history[nextIdx]);
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "l") {
      // Ctrl/Cmd+L clears the screen — like a real terminal.
      e.preventDefault();
      setLines([]);
    }
  };

  // Handle any click inside the terminal area — refocus input so users can
  // keep typing after scrolling / selecting / whatever.
  const focusInput = () => inputRef.current?.focus();

  // Ask the backend for tab-completion on the current input.
  // - Unique completion: replace input (do NOT submit).
  // - Multiple candidates: show them inline as a system note (do NOT echo
  //   the prompt line — that would look like Enter was pressed).
  // - Backend may return a partial LCP completion alongside candidates;
  //   in that case fill the input with the LCP AND show candidates below.
  const doTabCompletion = async () => {
    try {
      const { data } = await api.post(`/labs/session/${sessionId}/complete`, { command: input, device_id: deviceId });
      const completion = data?.completion || null;
      const candidates = data?.candidates || [];
      if (completion) {
        setInput(completion);
      }
      if (candidates.length > 1) {
        setLines((ls) => [
          ...ls,
          nextLine({ type: "out", text: candidates.join("  ") }),
        ]);
      }
    } catch {
      // Silently ignore completion errors — tab becomes a noop like real IOS
      // when nothing matches.
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col h-full bg-black border-t border-zinc-800 font-term text-sm"
      onMouseUp={focusInput}
      data-testid="terminal"
    >
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-zinc-900 text-[10px] text-zinc-500 uppercase tracking-[0.25em]">
        <span className="flex items-center gap-2">
          <span className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
            <span className="w-2 h-2 rounded-full bg-zinc-700" />
          </span>
          <span className="ml-1">
            {deviceLabel ? <span className="text-[#00FF41] mr-2">[{deviceLabel}]</span> : null}
            HypexIOS — {prompt.replace(/[>#]$/, "")} — 9600 8-N-1
          </span>
        </span>
        {busy && <span className="text-[#F59E0B]">processing…</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-0.5 leading-relaxed">
        {lines.map((l) => {
          if (l.type === "banner") {
            return (
              <pre key={l.id} className="text-zinc-500 whitespace-pre-wrap" data-testid="terminal-banner">
                {l.text}
              </pre>
            );
          }
          if (l.type === "cmd") {
            return (
              <div key={l.id} className="flex">
                <span className="text-[#00FF41] mr-2 shrink-0">{l.prompt}</span>
                <span className="text-zinc-100 whitespace-pre-wrap break-words">{l.text}</span>
              </div>
            );
          }
          if (l.type === "err") {
            return (
              <pre key={l.id} className="text-[#EF4444] whitespace-pre-wrap">{l.text}</pre>
            );
          }
          if (l.type === "more") {
            return (
              <div
                key={l.id}
                className="bg-zinc-100 text-black px-2 py-0.5 inline-block font-term"
                data-testid="terminal-more"
              >
                --More--
              </div>
            );
          }
          return (
            <pre key={l.id} className="text-zinc-300 whitespace-pre-wrap leading-relaxed">
              {l.text}
            </pre>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 px-4 py-2 border-t border-zinc-900">
        <span className="text-[#00FF41] shrink-0" data-testid="terminal-prompt">
          {isPaging ? "" : prompt}
        </span>
        <input
          ref={inputRef}
          value={isPaging ? "" : input}
          onChange={(e) => !isPaging && setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={isPaging ? "press SPACE for next page · ENTER for next line · any other key to quit" : ""}
          className="flex-1 bg-transparent text-zinc-100 outline-none border-none caret-[#00FF41] placeholder:text-zinc-600"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoFocus
          data-testid="terminal-input"
        />
        <span className="w-2 h-4 bg-[#00FF41] animate-caret-blink" aria-hidden />
      </form>
    </div>
  );
}
